/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { CSSProperties, Children, useEffect, useRef } from 'react';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import {
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  Mousewheel,
  FreeMode,
  Virtual,
} from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';

import { useCarouselContextV2 } from './CarouselV2ContextProvider';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/a11y';
import 'swiper/css/mousewheel';
import 'swiper/css/virtual';
import 'swiper/css/free-mode';

interface CarouselV2Props {
  /** An optional name for the carousel. */
  name?: string;
  /** Carousel child elements */
  children: React.ReactNode;
  /** Custom styles for the container */
  containerStyle?: CSSProperties;
  /** Custom styles for the slide items */
  slideStyle?: CSSProperties;
  /** Swiper options */
  swiperOptions?: SwiperProps & SwiperOptions;
  /**
   * Number of slides kept before/after the viewport when virtualization is enabled.
   * If provided, Swiper's Virtual module is activated.
   */
  virtualWindow?: number;
}

const swiperOptionsWithDefaults: SwiperProps & SwiperOptions = {
  spaceBetween: 16,
  direction: 'horizontal',
  slidesPerView: 'auto',
  navigation: false,
  freeMode: {
    enabled: true,
    sticky: true,
  },
  mousewheel: {
    forceToAxis: true, // Force scrolling only on horizontal axis
    sensitivity: 1,
    releaseOnEdges: false,
  },
  // Enable keyboard navigation
  keyboard: {
    enabled: true,
    onlyInViewport: true,
  },
  grabCursor: true,
  // Allow scrolling to the edges
  cssMode: false,
  // Ensure slides can be scrolled to the edge
  centeredSlides: false,
  centeredSlidesBounds: false,
  style: {
    zIndex: 0,
  },
};

/**
 * CarouselV2 - A reusable carousel component with swiping support
 *
 * Features:
 * - Deep nested arrow button support
 * - Built on Swiper.js for reliability
 */
export const CarouselV2 = ({
  children,
  containerStyle = {},
  slideStyle = {},
  swiperOptions = {},
  virtualWindow,
  name = 'Carousel',
}: CarouselV2Props): React.JSX.Element => {
  const carouseListRef = useRef<HTMLDivElement>(null);
  const { handleSwiper } = useCarouselContextV2();

  const _swiperOptions = {
    ...swiperOptionsWithDefaults,
    ...swiperOptions,
    style: {
      ...swiperOptionsWithDefaults.style,
      ...swiperOptions.style,
    },
  };

  // slidesPerView: 'auto' breaks with Virtual module → results in getComputedStyle error.
  if (
    typeof virtualWindow === 'number' &&
    _swiperOptions.slidesPerView === 'auto'
  ) {
    // default to 1 slide; override by passing a concrete number in props
    _swiperOptions.slidesPerView = 1;
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[CarouselV2] virtual slides do not support slidesPerView="auto". ' +
          'Falling back to 1. Pass a number yourself to silence this.',
      );
    }
  }

  useEffect(() => {
    const el = carouseListRef.current;

    const wheelHandler = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault(); // block browser nav gesture
      }
    };

    el?.addEventListener('wheel', wheelHandler, { passive: false });

    return () => el?.removeEventListener('wheel', wheelHandler);
  }, []);

  const swiperModules = [];
  if (_swiperOptions.navigation) swiperModules.push(Navigation);
  if (_swiperOptions.pagination) swiperModules.push(Pagination);
  if (_swiperOptions.scrollbar) swiperModules.push(Scrollbar);
  if (_swiperOptions.mousewheel) swiperModules.push(Mousewheel);
  if (_swiperOptions.freeMode) swiperModules.push(FreeMode);
  if (typeof virtualWindow === 'number') swiperModules.push(Virtual);

  swiperModules.push(A11y); // Always include accessibility

  const swiperConfig: SwiperOptions = {
    modules: swiperModules,
    ..._swiperOptions,
    ...(typeof virtualWindow === 'number' && {
      virtual: {
        enabled: true,
        addSlidesBefore: virtualWindow,
        addSlidesAfter: virtualWindow,
      },
    }),
  };

  return (
    <div
      data-module="CarouselList"
      ref={carouseListRef}
      style={{ ...containerStyle }}
    >
      <Swiper aria-label={name} {...swiperConfig} onSwiper={handleSwiper}>
        {Children.map(children, (child, index) => (
          <SwiperSlide
            key={index}
            style={slideStyle}
            {...(typeof virtualWindow === 'number'
              ? { virtualIndex: index }
              : {})}
          >
            {child}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default CarouselV2;
